/** @jsxImportSource ./i18n */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Portfolio from './pages/Portfolio';
import PEA from './pages/PEA';
import Rebalance from './pages/Rebalance';
import PanelScorecard from './pages/PanelScorecard';
import Dividends from './pages/Dividends';
import Analysis from './pages/Analysis';
import DailyReports from './pages/DailyReports';
import ExpertReports from './pages/ExpertReports';
import ExpertLive from './pages/ExpertLive';
import ReportsLayout from './components/ReportsLayout';
import History from './pages/History';
import Settings from './pages/Settings';
import Rumors from './pages/Rumors';
import LatestNews from './pages/LatestNews';
import Performance from './pages/Performance';
import StockDetail from './pages/StockDetail';
import { LanguageProvider } from './contexts/LanguageContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import { ToastProvider } from './contexts/ToastContext';
import { Spinner, Toaster } from './components/ui';

// Laboratory pages are loaded on demand.
const GhostPortfolio = lazy(() => import('./pages/GhostPortfolio'));
const Constellation = lazy(() => import('./pages/Constellation'));
const Exposure = lazy(() => import('./pages/Exposure'));
const Wrapped = lazy(() => import('./pages/Wrapped'));
const Journal = lazy(() => import('./pages/Journal'));
const Calm = lazy(() => import('./pages/Calm'));
const Goal = lazy(() => import('./pages/Goal'));
const Fees = lazy(() => import('./pages/Fees'));
const EarningsBriefings = lazy(() => import('./pages/EarningsBriefings'));
const Trial = lazy(() => import('./pages/Trial'));
const Multiverse = lazy(() => import('./pages/Multiverse'));
const CrisisSimulator = lazy(() => import('./pages/CrisisSimulator'));
const Tamagotchi = lazy(() => import('./pages/Tamagotchi'));
const TradingLayout = lazy(() => import('./components/trading/TradingLayout'));
const TradingHome = lazy(() => import('./pages/trading/TradingHome'));
const OrderTicket = lazy(() => import('./pages/trading/OrderTicket'));
const TradingJournal = lazy(() => import('./pages/trading/TradingJournal'));
const TradingSettings = lazy(() => import('./pages/trading/TradingSettings'));
const TradingPrograms = lazy(() => import('./pages/trading/TradingPrograms'));
const TradingPilot = lazy(() => import('./pages/trading/TradingPilot'));
const TradingLive = lazy(() => import('./pages/trading/TradingLive'));
const TradingSniper = lazy(() => import('./pages/trading/TradingSniper'));
const TradingArena = lazy(() => import('./pages/trading/TradingArena'));
const TradingReview = lazy(() => import('./pages/trading/TradingReview'));
const lab = (Page) => <Suspense fallback={<Spinner label="Chargement…" />}><Page /></Suspense>;

const DesignSystem = process.env.NODE_ENV !== 'production' ? lazy(() => import('./pages/DesignSystem')) : null;

const ProtectedRoute = ({ children }) => {
  const { token } = useSelector((state) => state.auth);
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { token } = useSelector((state) => state.auth);
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <LanguageProvider>
    <PrivacyProvider>
    <ToastProvider>
    <Routes>
      {DesignSystem && <Route path="/_design" element={<Suspense fallback={null}><DesignSystem /></Suspense>} />}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="pea" element={<PEA />} />
        <Route path="calme" element={lab(Calm)} />
        <Route path="objectif" element={lab(Goal)} />
        <Route path="frais" element={lab(Fees)} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="rebalance" element={<Rebalance />} />
        <Route path="dividends" element={<Dividends />} />
        <Route path="history" element={<History />} />
        <Route path="analysis" element={<Analysis />} />
        <Route path="reports" element={<ReportsLayout />}>
          <Route index element={<DailyReports />} />
          <Route path="experts" element={<ExpertReports />} />
          <Route path="experts/:runId" element={<ExpertReports />} />
          <Route path="live" element={<ExpertLive />} />
          <Route path="scorecard" element={<PanelScorecard />} />
          <Route path="earnings" element={lab(EarningsBriefings)} />
          <Route path=":reportId" element={<DailyReports />} />
        </Route>
        <Route path="news" element={<LatestNews />} />
        <Route path="performance" element={<Performance />} />
        <Route path="rumors" element={<Rumors />} />
        <Route path="ghost" element={lab(GhostPortfolio)} />
        <Route path="constellation" element={lab(Constellation)} />
        <Route path="exposition" element={lab(Exposure)} />
        <Route path="wrapped" element={lab(Wrapped)} />
        <Route path="journal" element={lab(Journal)} />
        <Route path="journal/:reportId" element={lab(Journal)} />
        <Route path="proces" element={lab(Trial)} />
        <Route path="proces/:runId" element={lab(Trial)} />
        <Route path="multivers" element={lab(Multiverse)} />
        <Route path="crise" element={lab(CrisisSimulator)} />
        <Route path="tamagotchi" element={lab(Tamagotchi)} />
        <Route path="trading" element={lab(TradingLayout)}>
          <Route index element={lab(TradingHome)} />
          <Route path="ordre" element={lab(OrderTicket)} />
          <Route path="live" element={lab(TradingLive)} />
          <Route path="pilote" element={lab(TradingPilot)} />
          <Route path="sniper" element={lab(TradingSniper)} />
          <Route path="arene" element={lab(TradingArena)} />
          <Route path="bilan" element={lab(TradingReview)} />
          <Route path="programmes" element={lab(TradingPrograms)} />
          <Route path="journal" element={lab(TradingJournal)} />
          <Route path="reglages" element={lab(TradingSettings)} />
        </Route>
        <Route path="stock/:symbol" element={<StockDetail />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
    <Toaster />
    </ToastProvider>
    </PrivacyProvider>
    </LanguageProvider>
  );
}

export default App;
